<template>
  <div data-events-v="3.2.4">
    <template v-if="data?.hasVisibilitySet && visibilityStatus === 'checking'">
      <div
        style="
          min-height: 480px;
          display: flex;
          align-items: center;
          justify-content: center;
        "
      >
        <LoaderDots />
      </div>
    </template>
    <template
      v-else-if="data?.hasVisibilitySet && visibilityStatus === 'unknown'"
    >
      <div
        style="
          min-height: 480px;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 0 20px;
        "
      >
        <div style="max-width: 480px; text-align: left">
          <h2 style="margin: 0 0 8px; font-size: 20px">
            We need one quick setting before we can show this event
          </h2>

          <p style="margin: 0 0 16px; line-height: 1.5">
            This page is restricted by region. We can’t confirm access until
            your country/region preference is set.
          </p>

          <div
            style="
              display: inline-flex;
              gap: 10px;
              justify-content: center;
              flex-wrap: wrap;
            "
          >
            <BaseButton type="button" @click="onRequestLocaleSetter">
              Set my region
            </BaseButton>
          </div>

          <p style="margin: 14px 0 0; font-size: 13px; opacity: 0.8">
            If you use a browser setting that blocks cookies, you may need to
            allow site preferences for this page to load correctly.
          </p>
        </div>
      </div>
    </template>
    <template v-else-if="visibilityStatus === 'visible'">
      <HeroTimeline
        v-if="data?.event?.fields?.hero"
        :data="data?.event?.fields?.hero"
      />

      <ContentJourneyFlyout
        :class="$style.mobileJourneyFlyout"
        :journeys="contentJourneys"
        :loading="isContentJourneyLoading"
        :active-journey-id="activeJourneyId"
      />

      <WithAsideCards
        :data="data?.event?.fields?.cards"
        style="--background-color: var(--white)"
      >
        <div :class="$style.bodyLayout">
          <aside
            v-if="contentJourneys.length > 0 || isContentJourneyLoading"
            :class="$style.navigationAside"
          >
            <div :class="$style.desktopJourneys">
              <ContentJourney
                v-for="journey in contentJourneys"
                :key="journey.id"
                :journey="journey"
                :initially-expanded="
                  shouldExpandContentJourney(journey.id, activeJourneyId)
                "
              />
              <ContentJourney
                v-if="isContentJourneyLoading && !contentJourneys.length"
                :loading="true"
              />
            </div>
          </aside>

          <div :class="$style.contentContainer">
            <BaseHeading :class="$style.header" size="h3">
              {{ data?.event?.fields?.header }}
            </BaseHeading>

            <div
              :data-entry-title="data?.event?.fields?.entryTitle || undefined"
            >
              <ArticleBody
                v-if="computedEventBody"
                :class="$style.copy"
                :data="computedEventBody"
              />
            </div>

            <div
              v-if="data?.event?.fields?.formats?.length"
              :class="$style.formats"
            >
              <BaseHeading size="h4" :class="$style.formatHeader">
                {{
                  localizedStrings['available-formats'] || 'Available Formats'
                }}
              </BaseHeading>

              <div :class="$style.formatGrid">
                <FormatCard
                  v-for="a in data?.event?.fields?.formats"
                  :key="a?.sys?.id"
                  :data="a"
                />
              </div>
            </div>
          </div>
        </div>
      </WithAsideCards>
      <ProductSet
        v-if="data?.event?.fields?.productSet"
        :data="data?.event?.fields?.productSet"
      />
      <BlueprintResolver
        v-if="data?.event?.fields?.blueprint?.length"
        :data="data?.event?.fields?.blueprint"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { arrayAsString } from '~/utils'
import { buildRedirectUrl } from '~/utils/parsePageLevel'
import { requireContentfulEntry } from '~/utils/requireContentfulEntry'
import { resolveUnauthorizedPath } from '~/utils/visibilityRedirect'
import { shouldExpandContentJourney } from '~/services/contentJourney'

import type { TypeEventSkeleton } from '~/types/contentful'

const { $client } = useNuxtApp()
const { locale } = useI18n()
const localePath = useLocalePath()
const { localizedStrings } = useLocalizedStrings()
const route = useRoute()
const slug = computed(() =>
  route.params?.slug ? arrayAsString(route.params.slug) : '',
)
const pathCandidates = computed(() =>
  [slug.value, `/${slug.value}`].filter((value): value is string =>
    Boolean(value),
  ),
)
const activeJourneyId = computed(() => arrayAsString(route.query.journey) || '')

const { data, error } = await useAsyncData(
  `event-response-${route.params?.slug}-${locale.value}`,
  async () => {
    const payload =
      await $client.withoutUnresolvableLinks.getEntries<TypeEventSkeleton>({
        content_type: 'event',
        limit: 1,
        include: 10,
        locale: locale.value,
        'fields.path[in]': pathCandidates.value,
        select: [
          'fields.entryTitle',
          'fields.metaTitle',
          'fields.metaDescription',
          'fields.metaImage',
          'fields.visibility',
          'fields.cards',
          'fields.copy',
          'fields.header',
          'fields.formats',
        ],
      })

    // Event has a field Hero, which references content model Timeline,
    // Timeline has a field, Events, which references content model Event,
    // causing a circular reference. In the above API call, 'fields.hero'
    // is excluded so that include can be set to 10 without causing a
    // stack-overflow. So here, we get Hero references separately so the
    // include can be set to 3, preventing circular reference issue
    const circularPayload =
      await $client.withoutUnresolvableLinks.getEntries<TypeEventSkeleton>({
        content_type: 'event',
        limit: 1,
        include: 3,
        locale: locale.value,
        'fields.path[in]': pathCandidates.value,
        select: ['fields.hero'],
      })

    const eventData = requireContentfulEntry(
      payload?.items?.[0],
      'Event not found',
    )
    const circularEventData = circularPayload?.items?.[0] ?? null

    return {
      event: {
        fields: {
          ...(eventData?.fields ?? {}),
          ...(circularEventData?.fields ?? {}),
        },
        sys: {
          ...(eventData?.sys ?? {}),
          ...(circularEventData?.sys ?? {}),
        },
        metadata: {
          ...(eventData?.metadata ?? {}),
          ...(circularEventData?.metadata ?? {}),
        },
      },
      hasVisibilitySet: !!eventData?.fields?.visibility,
      hasAccessLevelSet: !!eventData?.fields?.accessLevel,
    }
  },
  {
    watch: [() => locale.value],
  },
)

if (error.value) throw error.value

const { journeys: contentJourneys, isLoading: isContentJourneyLoading } =
  useContentJourney(() => data.value?.event)

watch(error, (asyncDataError) => {
  if (asyncDataError) showError(asyncDataError)
})

const computedEventBody = computed(() => {
  const typedMarkdownEditorReturn = data?.value?.event?.fields?.copy as {
    body: string
  }
  return typedMarkdownEditorReturn?.body || ''
})

if (data.value?.event) getPageMetaData(data.value?.event)

const { visibilityStatus } = usePageVisibility({
  visibility: () => data.value?.event?.fields?.visibility,
  onDenied: async (decision) => {
    const unauthorizedPath = resolveUnauthorizedPath(decision)
    if (unauthorizedPath) {
      await navigateTo({
        path: localePath(unauthorizedPath),
        query:
          unauthorizedPath === '/unauthorized'
            ? { returnTo: route.fullPath }
            : undefined,
      })
      return
    }

    const redirectUrl = buildRedirectUrl(
      locale.value,
      data.value?.event?.fields?.accessDenialRedirectUrl || '/events',
    )

    return navigateTo(redirectUrl)
  },
})

const onRequestLocaleSetter = () => {
  const localeModalStore = useLocalModalStore()
  localeModalStore.showLocaleModal = true
}

onMounted(() => {
  pushToGaDataLayer({
    event: 'ga4.trackEvent',
    eventName: 'event_view',
    eventParams: {
      event_name: data?.value?.event?.fields?.entryTitle,
    },
  })
})

const globalStore = useGlobalStore()
watch(
  () => data.value,
  (newVal) => {
    globalStore.pageEntrySys = newVal?.event?.sys
  },
  { immediate: true },
)
</script>

<style lang="scss" module>
.bodyLayout {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 2rem;

  @include media('lg') {
    flex-direction: row;
    gap: 3%;
  }
}

.navigationAside {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;

  @include media('less-than-lg') {
    display: contents;
  }

  @include media('lg') {
    align-self: stretch;
    flex: 0 0 340px;
  }
}

.desktopJourneys {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @include media('less-than-lg') {
    display: none;
  }
}

.mobileJourneyFlyout {
  display: block;

  @include media('lg') {
    display: none;
  }
}

.contentContainer {
  min-width: 0;
  width: 100%;
}

.headerContainer {
  max-width: 544px;
  margin: 0 auto 22px;

  @include media('sm') {
    margin-left: 0;
  }
}

.header {
  color: var(--black);
  margin-bottom: 19px;
}

.subheader {
  @include fluid-type(16px, 22px);
  font-family: var(--georgia);
  line-height: math.div(32, 22);
}

.copy {
  margin-bottom: 20px;
}

.formats {
  border-top: 1px solid var(--gray-4);
  padding-top: 20px;
  margin-top: 60px;
}

.formatHeader {
  margin-bottom: 20px;
}

.formatGrid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 26px;

  @include media('xs') {
    grid-template-columns: repeat(2, 1fr);
  }

  @include media('sm') {
    grid-template-columns: repeat(3, 1fr);
  }
}
</style>
